import { useState } from "react";
import { CalendarOff, Layers, Zap, Flame, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/Button";
import { LocationPicker } from "@/components/ui/LocationPicker";
import { ApiError, apiFetch, useApiMutation } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { useMeasurements } from "@/hooks/useMeasurements";
import { useBillingPeriod } from "@/hooks/useBillingPeriod";
import { periodIdFromDate } from "@/lib/format";
import { PeriodSelector } from "@/routes/invoicing/PeriodSelector";
import { MeasurementsGrid } from "./measurements/MeasurementsGrid";
import { CollectionLog, SessionLogEntry } from "./measurements/CollectionLog";
import { EditMeasurementModal } from "./measurements/EditMeasurementModal";
import { BulkEditModal } from "./measurements/BulkEditModal";
import { MeasurementRow } from "@/types/measurements";

export default function Measurements() {
  const [periodId, setPeriodId] = useState(() => periodIdFromDate(new Date()));
  const [location, setLocation] = useState<string | null>(null);
  const { data, loading, error, refetch } = useMeasurements(periodId);
  const { data: period } = useBillingPeriod(periodId);
  const [editing, setEditing] = useState<MeasurementRow | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const { mutate, loading: triggering } = useApiMutation();
  const { push } = useToast();
  const [sessionLog, setSessionLog] = useState<SessionLogEntry[]>([]);
  const [triggeringTauron, setTriggeringTauron] = useState(false);
  const [triggeringPgnig, setTriggeringPgnig] = useState(false);

  function addLog(message: string, tone: SessionLogEntry["tone"] = "success") {
    setSessionLog((prev) => [...prev, { ts: new Date().toISOString(), message, tone }]);
  }

  async function triggerProvider(provider: "tauron" | "pgnig" | null) {
    const name = provider === "tauron" ? "Tauron" : provider === "pgnig" ? "PGNiG" : "wszystkich dostawców";
    if (provider === "tauron") setTriggeringTauron(true);
    else if (provider === "pgnig") setTriggeringPgnig(true);
    addLog(`Uruchamianie pobierania z ${name}…`, "muted");
    try {
      const body: Record<string, string> = { period_id: periodId };
      if (provider) body.provider = provider;
      await mutate("/api/billing/collection/trigger", "POST", body);
      addLog(`Pobieranie z ${name} uruchomione — wyniki pojawią się w dzienniku za chwilę`);
      setTimeout(() => { refetch(); }, 30000);
    } catch (err) {
      addLog(`Błąd pobierania z ${name}: ${(err as Error).message}`, "danger");
      push({ message: (err as Error).message, tone: "danger" });
    } finally {
      if (provider === "tauron") setTriggeringTauron(false);
      else if (provider === "pgnig") setTriggeringPgnig(false);
    }
  }

  async function deleteRow(row: MeasurementRow) {
    const [year, month] = periodId.split("-").map(Number);
    try {
      await apiFetch("/api/billing/collection/entry", {
        method: "DELETE",
        body: JSON.stringify({ location: row.location, utility: row.utility, year, month }),
      });
      addLog(`Zresetowano ${row.utility} (${row.location}) — następne inkaso pobierze dane od nowa`);
      push({ message: `Usunięto ${row.utility} (${row.location})`, tone: "success" });
      refetch();
    } catch (err) {
      addLog(`Błąd usuwania ${row.utility} (${row.location}): ${(err as Error).message}`, "danger");
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  function handleEditSaved(logLabel?: string) {
    if (logLabel) addLog(logLabel);
    refetch();
  }

  // Filter rows by selected location
  const allRows = data?.rows ?? [];
  const visibleRows = location ? allRows.filter((r) => r.location === location) : allRows;

  if (loading) return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-border h-12" />
      <Skeleton className="h-64 w-full" />
    </div>
  );

  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-border">
          <div className="w-36"><PeriodSelector value={periodId} onChange={(id) => { setPeriodId(id); setLocation(null); }} /></div>
          <LocationPicker value={location} onChange={setLocation} />
        </div>
        <EmptyState
          icon={<CalendarOff className="size-8" />}
          title="Brak okresu rozliczeniowego"
          description="Dla wybranego miesiąca nie ma jeszcze rekordu billing_period."
        />
      </div>
    );
  }

  if (error || !data) {
    return <EmptyState title="Nie udało się pobrać pomiarów" description={error?.message ?? "Nieznany błąd"} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Control bar — matches Rozliczenie header layout */}
      <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-border">
        <div className="w-36">
          <PeriodSelector value={periodId} onChange={(id) => { setPeriodId(id); setLocation(null); }} />
        </div>
        <LocationPicker value={location} onChange={setLocation} />
        <div className="flex-1" />
        <Button variant="secondary" iconLeft={<Layers className="size-3.5" />} onClick={() => setBulkOpen(true)}>
          Edycja zbiorcza
        </Button>
      </div>

      {/* Provider fetch buttons */}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" iconLeft={<Zap className="size-3.5" />}
                loading={triggeringTauron}
                onClick={() => triggerProvider("tauron")}>
          Pobierz z Tauron
        </Button>
        <Button variant="secondary" iconLeft={<Flame className="size-3.5" />}
                loading={triggeringPgnig}
                onClick={() => triggerProvider("pgnig")}>
          Pobierz z PGNiG
        </Button>
        <Button iconLeft={<RefreshCw className="size-3.5" />}
                loading={triggering}
                onClick={() => triggerProvider(null)}>
          Pobierz opłaty za media
        </Button>
      </div>

      <MeasurementsGrid rows={visibleRows} onEdit={(r) => setEditing(r)} onDelete={deleteRow} />

      <CollectionLog
        entries={period?.auto_collection_log ?? []}
        sessionEntries={sessionLog}
      />

      <EditMeasurementModal
        open={!!editing}
        periodId={periodId}
        row={editing}
        rows={allRows}
        onClose={() => setEditing(null)}
        onSaved={handleEditSaved}
      />

      <BulkEditModal
        open={bulkOpen}
        periodId={periodId}
        rows={visibleRows}
        onClose={() => setBulkOpen(false)}
        onSaved={refetch}
      />
    </div>
  );
}
